<template>
    <!-- 不能用 p-2：它带 !important，会盖掉 AppContent 给页头留的 padding-top，导致页头遮住查询行 -->
    <common-page flex-column content-class="opc-point-curve px-2 pb-2">
        <!-- 查询行：设备编码 + 时间段 + 查询；由炉子加热工单跳入时显示记录编号 -->
        <div class="card mb-2 flex-shrink-0">
            <div class="card-body py-2 px-3">
                <form class="opc-point-curve__query" @submit.prevent="query()">
                    <div class="input-group input-group-sm opc-point-curve__device">
                        <div class="input-group-prepend">
                            <label class="input-group-text" for="opcPointCurveDeviceCode">
                                <i class="fa fa-fw fa-microchip mr-1"></i>{{ $t('label.sith.tpm.opcPointCurve.deviceCode') }}
                            </label>
                        </div>
                        <!-- 可搜索下拉（订阅表里配过的设备），也可输入列表外的编码；选中 / 回车即查询 -->
                        <v-select class="opc-point-curve__device-select" input-id="opcPointCurveDeviceCode"
                                  :options="deviceOptions" label="deviceCode" :reduce="option => option.deviceCode"
                                  taggable :create-option="createDeviceOption" :filter-by="filterDevice"
                                  :placeholder="$t('label.sith.tpm.opcPointCurve.deviceCodePlaceholder')"
                                  :value="queryState.deviceCode || null" @input="onDeviceInput">
                            <template #option="option">
                                <div class="d-flex align-items-center">
                                    <span class="text-nowrap">{{ option.deviceCode }}</span>
                                    <span class="text-muted ml-2 text-truncate">{{ option.deviceName }}</span>
                                    <span v-if="option.pointCount" class="small text-muted ml-auto pl-2 text-nowrap">
                                        {{ $t('label.sith.tpm.opcPointCurve.devicePointCount', {count: option.pointCount}) }}
                                    </span>
                                </div>
                            </template>
                            <template #selected-option="option">
                                <span class="text-nowrap">{{ option.deviceCode }}</span>
                                <span v-if="option.deviceName" class="text-muted ml-1 text-truncate">{{ option.deviceName }}</span>
                            </template>
                            <template #no-options>
                                <span class="small text-muted">{{ $t('label.sith.tpm.opcPointCurve.noDeviceMatch') }}</span>
                            </template>
                        </v-select>
                    </div>
                    <div class="input-group input-group-sm opc-point-curve__range">
                        <div class="input-group-prepend">
                            <label class="input-group-text" for="opcPointCurveStart">
                                <i class="fa fa-fw fa-clock-o mr-1"></i>{{ $t('label.sith.tpm.opcPointCurve.timeRange') }}
                            </label>
                        </div>
                        <input id="opcPointCurveStart" type="datetime-local" class="form-control"
                               :title="$t('label.sith.tpm.opcPointCurve.start')"
                               :aria-label="$t('label.sith.tpm.opcPointCurve.start')"
                               v-model="queryState.start">
                        <div class="input-group-prepend opc-point-curve__range-sep">
                            <span class="input-group-text">~</span>
                        </div>
                        <input id="opcPointCurveEnd" type="datetime-local" class="form-control"
                               :title="$t('label.sith.tpm.opcPointCurve.end')"
                               :aria-label="$t('label.sith.tpm.opcPointCurve.end')"
                               v-model="queryState.end">
                    </div>
                    <b-button type="submit" size="sm" variant="primary" class="opc-point-curve__submit" :disabled="loadingPoints">
                        <i class="fa fa-fw" :class="loadingPoints ? 'fa-spinner fa-spin' : 'fa-search'"></i>
                        {{ $t('label.sith.tpm.opcPointCurve.query') }}
                    </b-button>
                    <span v-if="appliedRecord" class="badge badge-info opc-point-curve__record" :title="recordTitle">
                        <i class="fa fa-fw fa-fire"></i>{{ $t('label.sith.tpm.opcPointCurve.record') }} {{ appliedRecord.queryKey }}
                    </span>
                </form>
            </div>
        </div>

        <div class="opc-point-curve__body">
            <!-- 设备点位 -->
            <div class="card opc-point-curve__points">
                <div class="card-header py-2 d-flex align-items-center">
                    <span class="font-weight-bold text-nowrap">{{ $t('label.sith.tpm.opcPointCurve.points') }}</span>
                    <span v-if="device" class="small text-muted ml-2 text-truncate" :title="deviceTitle">{{ deviceTitle }}</span>
                    <span v-if="device && devicePoints.length" class="small text-muted ml-auto pl-2 text-nowrap">
                        {{ queryState.points.length }} / {{ selectableCount }}
                    </span>
                </div>
                <div class="card-body p-0 opc-point-curve__points-body">
                    <b-overlay :show="loadingPoints" rounded="sm" class="h-100">
                        <table v-if="devicePoints.length" class="table table-sm table-hover mb-0 opc-point-curve__table">
                            <thead>
                            <tr>
                                <th class="text-nowrap"></th>
                                <th class="text-nowrap">{{ $t('label.sith.tpm.opcPointCurve.pointName') }}</th>
                                <th class="text-nowrap">{{ $t('label.sith.tpm.opcPointCurve.nodeId') }}</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(point, index) in devicePoints" :key="point.nodeId"
                                :class="{'text-muted': !point.hasHistory}"
                                :title="point.hasHistory ? '' : $t('label.sith.tpm.opcPointCurve.noHistory')">
                                <td class="align-middle">
                                    <input :id="checkboxId(index)" type="checkbox" :value="point.nodeId"
                                           :disabled="!point.hasHistory" v-model="queryState.points">
                                </td>
                                <td class="align-middle">
                                    <label :for="checkboxId(index)" class="mb-0 d-flex align-items-center"
                                           :class="point.hasHistory ? 'opc-point-curve__clickable' : ''">
                                        <span class="opc-point-curve__swatch mr-1"
                                              :style="{background: point.hasHistory ? colorOf(point.nodeId) : 'transparent'}"></span>
                                        <span class="text-break">{{ point.name }}</span>
                                    </label>
                                    <span v-if="!point.hasHistory" class="badge badge-light border">
                                        {{ $t('label.sith.tpm.opcPointCurve.noHistory') }}
                                    </span>
                                </td>
                                <td class="align-middle small text-monospace opc-point-curve__node-id">{{ point.nodeId }}</td>
                            </tr>
                            </tbody>
                        </table>
                        <div v-else class="text-muted text-center small py-4 px-2">{{ pointsPlaceholder }}</div>
                    </b-overlay>
                </div>
            </div>

            <!-- 趋势曲线 -->
            <div class="card opc-point-curve__chart-card">
                <div class="card-header py-2 d-flex align-items-center flex-wrap">
                    <span class="font-weight-bold mr-2 text-nowrap">{{ $t('label.sith.tpm.opcPointCurve.chart') }}</span>
                    <span v-if="appliedRangeText" class="small text-muted mr-2">{{ appliedRangeText }}</span>
                    <span v-if="detailLoading" class="small text-muted mr-2 text-nowrap">
                        <i class="fa fa-fw fa-spinner fa-spin"></i>{{ $t('label.sith.tpm.opcPointCurve.detailLoading') }}
                    </span>
                    <div class="ml-auto d-flex align-items-center flex-wrap">
                        <!-- 导出针对当前曲线（已查询的范围 + 勾选点位），放在曲线卡片上 -->
                        <b-button-group size="sm" class="mr-2">
                            <b-button variant="outline-success" :disabled="!canExport || exporting" @click="exportExcel">
                                <i class="fa fa-fw" :class="exporting ? 'fa-spinner fa-spin' : 'fa-file-excel-o'"></i>
                                {{ $t('label.sith.tpm.opcPointCurve.exportExcel') }}
                            </b-button>
                            <b-button variant="outline-danger" :disabled="!canExportPdf" @click="openPdfModal">
                                <i class="fa fa-fw fa-file-pdf-o"></i>
                                {{ $t('label.sith.tpm.opcPointCurve.exportPdf') }}
                            </b-button>
                        </b-button-group>
                        <b-button size="sm" variant="outline-secondary" class="mr-2" :disabled="!appliedRange"
                                  @click="resetView">
                            <i class="fa fa-fw fa-refresh"></i>
                            {{ $t('label.sith.tpm.opcPointCurve.resetView') }}
                        </b-button>
                        <opc-curve-settings-popover v-model="queryState.settings" :auto-unit="autoUnit"
                                                    :xinterval-fallback="xintervalFallback"></opc-curve-settings-popover>
                    </div>
                </div>
                <div class="card-body p-2 d-flex flex-column opc-point-curve__chart-body">
                    <div v-if="yRangeInvalid" class="alert alert-warning py-1 px-2 mb-2 small">
                        <i class="fa fa-fw fa-exclamation-triangle"></i>
                        {{ $t('label.sith.tpm.opcPointCurve.yRangeInvalid') }}
                    </div>
                    <b-overlay :show="historyInFlight > 0" rounded="sm" class="opc-point-curve__chart-wrap">
                        <opc-curve-chart ref="chart" class="h-100"
                                         :series="chartSeries" :range="appliedRange" :settings="queryState.settings"
                                         :axis-unit="axisUnit" :placeholder="chartPlaceholder"
                                         @view-change="onViewChange"
                                         @x-interval-fallback="xintervalFallback = $event"></opc-curve-chart>
                    </b-overlay>
                </div>
                <div class="card-footer py-1 small text-muted">
                    <i class="fa fa-fw fa-info-circle"></i>{{ $t('label.sith.tpm.opcPointCurve.hint') }}
                </div>
            </div>
        </div>

        <opc-curve-pdf-modal ref="pdfModal"></opc-curve-pdf-modal>
    </common-page>
</template>

<script>
/* eslint-env browser */
import CommonPage from "@/components/echarts-part/stuff/CommonPage.vue";
import OpcCurveChart from "@/components/SithMes/tpm/opcCurve/OpcCurveChart.vue";
import OpcCurveSettingsPopover from "@/components/SithMes/tpm/opcCurve/OpcCurveSettingsPopover.vue";
import OpcCurvePdfModal from "@/components/SithMes/tpm/opcCurve/OpcCurvePdfModal.vue";
import {exportOpcCurveExcel} from "@/components/SithMes/tpm/opcCurve/opcCurveExcel";
import {fetchRecord} from "@/components/SithMes/tpm/opcCurve/opcCurveQueryApi";
import {
    defaultCurveSettings,
    parseInputTime,
    SPAN_UNIT_MS,
    toNumberOrNull,
    toInputTime,
    toServerTime,
    yRangeInvalid
} from "@/components/SithMes/tpm/opcCurve/opcCurveSettings";

const DAY_MS = 24 * 60 * 60 * 1000;
// 与后端 EmOpcPointCurveServiceImpl.MAX_WINDOWS_PER_SERIES 一致：画图时每条曲线按跨度分这么多个窗口
const MAX_WINDOWS = 2000;
// 补取细节时视窗两侧各多取的比例（小幅平移不必重取）
const DETAIL_MARGIN = 0.1;
const DEFAULT_DAYS = 3;
// 曲线颜色按点位在设备列表里的序号取，勾选变化不换色
const PALETTE = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];

function defaultRange() {
    const end = new Date();
    end.setSeconds(0, 0);
    return [new Date(end.getTime() - DEFAULT_DAYS * DAY_MS), end];
}

function pad2(n) {
    return n < 10 ? '0' + n : '' + n;
}

// 整段（overview）在 [from, to] 外的点 + 细节（detail）在 [from, to] 内的点；两者都按时间升序
function mergeDetail(overview, detail, from, to) {
    const before = overview.filter(p => p[0] < from);
    const after = overview.filter(p => p[0] > to);
    return before.concat(detail, after);
}

function displayTime(date) {
    return date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate())
        + ' ' + pad2(date.getHours()) + ':' + pad2(date.getMinutes());
}

/**
 * OPC 点位曲线（验收 #1–#10）：设备编码 → 点位 → 勾选 + 时间范围 → InfluxDB 历史多曲线 → 导出 Excel / 导出 PDF。
 * 由炉子加热工单页通过 ?recordId= 跳入：回填设备编码与时间段、自动查询并勾选该设备全部有历史的点位。
 * <p>
 * 接口：em-base-opc-server 的 GET /emOpcPointCurve/devices、/points、POST /emOpcPointCurve/history；
 * sith-mes-server 的 /sithMesTpmOpcCurveQuery/*（炉子加热工单记录，见 opcCurveQueryApi.js）。
 * <p>
 * 精度自适应：history（raw=false）按查询跨度分 2000 个窗口，每窗口回传最小、最大两个原始点（everyMs = 窗口）。查询后先画整段（overview）；
 * 图表缩放停下后，若视窗理想窗口比 overview 的细一倍以上，就带 viewStart/viewEnd 补取视窗（两侧各多 10%）的细节（detail），
 * 视窗内用细节、视窗外用整段拼成一条曲线。重新查询 / 换勾选都会作废细节。
 * <p>
 * 单位：点位的 unit 来自 opc 控制台点位属性。纵轴名称 = 手动单位（settings.yunit）或勾选点位的共同单位；勾选点位单位不一致时
 * 纵轴不写单位、图例名后带各自单位。
 * <p>
 * 契约（字段名不得改）：
 *   queryState = { deviceCode, start: 'yyyy-MM-ddTHH:mm', end: 同, points: [nodeId...], settings: {span, spanUnit, ymin, ymax, yunit, xinterval} }
 *   回填并查询：改好 queryState 后调 query({points: [...] | 'all', appliedRecord})，按给定点位勾选（只保留有历史的）
 *   当前视图图片：this.$refs.chart.getViewImage()
 *   appliedRecord = { id, queryKey, orderCode, operationSeq, projectCode, materialCode } | null：由 ?recordId= 加载的炉子加热工单记录，
 *                  导出 PDF 弹窗据此预填；每次查询成功都会重设（手动查询清空）
 */
export default {
    name: "SithMesTpmOpcPointCurve",
    components: {
        CommonPage,
        OpcCurveChart,
        OpcCurveSettingsPopover,
        OpcCurvePdfModal
    },
    data() {
        const range = defaultRange();
        return {
            queryState: {
                deviceCode: '',
                start: toInputTime(range[0]),
                end: toInputTime(range[1]),
                points: [],
                settings: defaultCurveSettings()
            },
            // 最近一次查到的设备 { deviceCode, deviceName, points: [{nodeId, name, eocId, hasHistory}] }
            device: null,
            // 最近一次「查询」时的时间范围 [Date, Date]：曲线、勾选补查、导出都用它，而不是输入框里尚未查询的值
            appliedRange: null,
            // nodeId → history 接口返回的序列（当前 appliedRange 下）
            seriesByNode: {},
            loadingPoints: false,
            historyInFlight: 0,
            exporting: false,
            // 由炉子加热工单 ?recordId= 加载的记录，导出 PDF 预填工单号 / 工序号 / 项目号 / 物料号；手动查询后清空
            appliedRecord: null,
            // 设备下拉：GET /emOpcPointCurve/devices [{deviceCode, deviceName, pointCount}]
            deviceOptions: [],
            // 缩放补取的细节 { range: [fromMs, toMs], everyMs, byNode: nodeId → points }；null = 只画整段
            detail: null,
            detailLoading: false,
            // 图表报告：设了刻度间隔但视窗内刻度过多，已按默认刻度显示
            xintervalFallback: false
        };
    },
    computed: {
        devicePoints() {
            return this.device ? this.device.points : [];
        },
        selectableCount() {
            return this.devicePoints.filter(p => p.hasHistory).length;
        },
        deviceTitle() {
            if (!this.device) {
                return '';
            }
            return [this.device.deviceCode, this.device.deviceName].filter(Boolean).join(' ');
        },
        // 勾选点位的单位（未填为 ''）去重
        checkedUnits() {
            const units = [];
            this.checkedInOrder.forEach(p => {
                const unit = p.unit || '';
                if (units.indexOf(unit) === -1) {
                    units.push(unit);
                }
            });
            return units;
        },
        // 勾选点位单位不一致（有填有不填也算）：纵轴不写单位，图例名后带各自单位
        unitsMixed() {
            return this.checkedUnits.length > 1;
        },
        // 勾选点位的共同单位；不一致或都没填为 ''
        autoUnit() {
            return this.checkedUnits.length === 1 ? this.checkedUnits[0] : '';
        },
        axisUnit() {
            const manual = (this.queryState.settings.yunit || '').trim();
            return manual || this.autoUnit;
        },
        // 整段数据的窗口（各序列同一次查询范围，取最大的）；没有数据时 null
        overviewEveryMs() {
            let every = null;
            this.checkedInOrder.forEach(p => {
                const s = this.seriesByNode[p.nodeId];
                if (s && s.everyMs && (every === null || s.everyMs > every)) {
                    every = s.everyMs;
                }
            });
            return every;
        },
        // 同名点位在图例里会被 echarts 合并，重名时带上节点 ID
        displayNames() {
            const counts = {};
            this.devicePoints.forEach(p => {
                counts[p.name] = (counts[p.name] || 0) + 1;
            });
            const names = {};
            this.devicePoints.forEach(p => {
                names[p.nodeId] = counts[p.name] > 1 ? p.name + '（' + p.nodeId + '）' : p.name;
            });
            return names;
        },
        pointIndex() {
            const index = {};
            this.devicePoints.forEach((p, i) => {
                index[p.nodeId] = i;
            });
            return index;
        },
        checkedInOrder() {
            return this.devicePoints.filter(p => p.hasHistory && this.queryState.points.indexOf(p.nodeId) !== -1);
        },
        chartSeries() {
            const detail = this.detail;
            return this.checkedInOrder
                .filter(p => this.seriesByNode[p.nodeId])
                .map(p => {
                    const overview = this.seriesByNode[p.nodeId];
                    const fine = detail && detail.byNode[p.nodeId];
                    return Object.assign({}, overview, {
                        points: fine ? mergeDetail(overview.points || [], fine, detail.range[0], detail.range[1]) : overview.points,
                        name: this.displayNames[p.nodeId] + (this.unitsMixed && p.unit ? '（' + p.unit + '）' : ''),
                        unit: p.unit || '',
                        color: this.colorOf(p.nodeId)
                    });
                });
        },
        hasChartData() {
            return this.chartSeries.some(s => s.points && s.points.length);
        },
        canExport() {
            return !!(this.device && this.appliedRange && this.checkedInOrder.length && this.hasChartData
                && this.historyInFlight === 0);
        },
        // 导出 PDF：曲线上有东西画着（且没有在途的历史请求）才可用
        canExportPdf() {
            return !!(this.device && this.appliedRange && this.hasChartData && this.historyInFlight === 0
                && !this.loadingPoints);
        },
        yRangeInvalid() {
            return yRangeInvalid(this.queryState.settings);
        },
        recordTitle() {
            const r = this.appliedRecord;
            return r ? [r.orderCode, r.operationSeq, r.projectCode, r.materialCode].filter(Boolean).join(' / ') : '';
        },
        appliedRangeText() {
            if (!this.appliedRange) {
                return '';
            }
            return displayTime(this.appliedRange[0]) + ' ~ ' + displayTime(this.appliedRange[1]);
        },
        pointsPlaceholder() {
            if (this.loadingPoints) {
                return '';
            }
            if (!this.device) {
                return this.$t('label.sith.tpm.opcPointCurve.pointsPlaceholder');
            }
            return this.noPointsMessage();
        },
        chartPlaceholder() {
            if (this.historyInFlight > 0 || this.loadingPoints) {
                return '';
            }
            if (!this.device || !this.appliedRange) {
                return this.$t('label.sith.tpm.opcPointCurve.chartPlaceholder');
            }
            if (!this.devicePoints.length) {
                return this.noPointsMessage();
            }
            if (!this.checkedInOrder.length) {
                return this.$t('label.sith.tpm.opcPointCurve.checkPoints');
            }
            return this.$t('label.sith.tpm.opcPointCurve.noData');
        }
    },
    watch: {
        // 勾选变化：只补查新勾上的点位（同一查询范围内已查过的直接复用），整段到齐后按当前视窗补细节
        'queryState.points'() {
            this.syncHistory().then(() => this.syncDetail());
        },
        // 同一页面内从 A 记录跳 B 记录时组件复用，所以用 watch 而不只在 created
        '$route.query.recordId': {
            immediate: true,
            handler(recordId) {
                this.loadRecord(recordId);
            }
        }
    },
    created() {
        // 非响应式：历史请求的代次（重新查询后旧响应作废）与在途的节点
        // （recordSeq 不在此初始化：$route.query.recordId 的 immediate watcher 先于 created 执行，由 loadRecord 兜底）
        this.historySeq = 0;
        this.pendingNodes = new Set();
        // 细节请求的代次与图表最近一次报告的视窗 [startMs, endMs]
        this.detailSeq = 0;
        this.lastView = null;
        this.loadDevices();
    },
    methods: {
        loadDevices() {
            this.$store.dispatch('api/get', {
                url: process.env.VUE_APP_EM_OPCCLIENT_URL + '/emOpcPointCurve/devices'
            }).then(list => {
                this.deviceOptions = Array.isArray(list) ? list : [];
            }).catch(error => {
                // 下拉取不到不影响手输编码查询；接口错误 api/get 已统一提示
                if (!error || (!error.response && !error.request)) {
                    console.error(error);
                }
            });
        },
        createDeviceOption(code) {
            return {deviceCode: String(code || '').trim(), deviceName: '', pointCount: 0};
        },
        // 编码或名称包含输入（忽略大小写）
        filterDevice(option, label, search) {
            const text = (search || '').trim().toLowerCase();
            return !text || [option.deviceCode, option.deviceName].some(v => v && String(v).toLowerCase().indexOf(text) !== -1);
        },
        // 选中 / 回车输入新编码即查询；清空只清编码
        onDeviceInput(code) {
            this.queryState.deviceCode = code ? String(code).trim() : '';
            if (this.queryState.deviceCode) {
                this.query();
            }
        },
        onViewChange(view) {
            this.lastView = view;
            this.syncDetail();
        },
        /**
         * 按图表当前视窗补取细节：理想窗口（视窗 / 2000）比整段的细一倍以上才取；已有细节覆盖视窗、点位齐全且够细则复用
         */
        syncDetail() {
            const view = this.lastView;
            const overviewEvery = this.overviewEveryMs;
            if (!view || !this.device || !this.appliedRange || !overviewEvery) {
                this.clearDetail();
                return;
            }
            const ideal = Math.ceil((view[1] - view[0]) / MAX_WINDOWS);
            if (ideal * 2 > overviewEvery) {
                this.clearDetail();
                return;
            }
            const nodeIds = this.checkedInOrder.filter(p => this.seriesByNode[p.nodeId]).map(p => p.nodeId);
            if (!nodeIds.length) {
                this.clearDetail();
                return;
            }
            const d = this.detail;
            if (d && d.range[0] <= view[0] && d.range[1] >= view[1] && ideal * 2 > d.everyMs
                && nodeIds.every(id => d.byNode[id])) {
                return;
            }
            const margin = (view[1] - view[0]) * DETAIL_MARGIN;
            // 后端时间精确到秒：起点向下、终点向上取整秒，保证覆盖视窗
            const from = Math.floor(Math.max(this.appliedRange[0].getTime(), view[0] - margin) / 1000) * 1000;
            const to = Math.ceil(Math.min(this.appliedRange[1].getTime(), view[1] + margin) / 1000) * 1000;
            if (from >= to) {
                return;
            }
            const seq = ++this.detailSeq;
            this.detailLoading = true;
            this.$store.dispatch('api/post', {
                url: process.env.VUE_APP_EM_OPCCLIENT_URL + '/emOpcPointCurve/history',
                data: {
                    nodeIds,
                    start: toServerTime(this.appliedRange[0]),
                    end: toServerTime(this.appliedRange[1]),
                    viewStart: toServerTime(new Date(from)),
                    viewEnd: toServerTime(new Date(to)),
                    raw: false
                },
                commonErrorHandle: false,
                consoleLog: false
            }).then(response => {
                if (seq !== this.detailSeq) {
                    return;
                }
                const list = (response && response.data) || [];
                const byNode = {};
                let everyMs = null;
                list.forEach(s => {
                    byNode[s.nodeId] = s.points || [];
                    everyMs = s.everyMs || everyMs;
                });
                // 后端跳过的点位记空细节，免得每次缩放都重取
                nodeIds.forEach(id => {
                    if (!byNode[id]) {
                        byNode[id] = [];
                    }
                });
                this.detail = {range: [from, to], everyMs: everyMs || ideal, byNode};
            }).catch(error => {
                if (seq !== this.detailSeq) {
                    return;
                }
                const response = error && error.response;
                if (response && response.status === 400 && response.data && response.data.message) {
                    this.toast(response.data.message);
                } else {
                    this.$store.dispatch('api/error', {error});
                }
            }).finally(() => {
                if (seq === this.detailSeq) {
                    this.detailLoading = false;
                }
            });
        },
        clearDetail() {
            this.detailSeq++;
            this.detailLoading = false;
            this.detail = null;
        },
        checkboxId(index) {
            return 'opcPointCurvePoint' + index;
        },
        colorOf(nodeId) {
            const index = this.pointIndex[nodeId];
            return PALETTE[(index === undefined ? 0 : index) % PALETTE.length];
        },
        // 设备档案里有这个编码 → 「没有关联点位」；没有 → 「查不到」
        noPointsMessage() {
            const code = this.device ? this.device.deviceCode : '';
            return this.device && this.device.deviceName
                ? this.$t('label.sith.tpm.opcPointCurve.deviceNoPoints', {code})
                : this.$t('label.sith.tpm.opcPointCurve.deviceNotFound', {code});
        },
        toast(message, variant) {
            this.$bvToast.toast(message, {
                title: this.$t('title.sithMes.tpm.opcPointCurve'),
                variant: variant || 'warning',
                solid: true,
                autoHideDelay: 4000
            });
        },
        /**
         * 查询：查设备点位 → 定勾选 → 查历史。
         * @param options.points 指定勾选（旧版保存的点位）；传 'all' 勾全部有历史的点位（由工单记录跳入时用）；
         *        不传时换设备默认勾第一个有历史的点位，同设备保留原勾选
         * @param options.appliedRecord 由工单记录跳入时传 {id, queryKey, orderCode, operationSeq, projectCode, materialCode}；
         *        不传（手动查询）则查询成功后清空 appliedRecord，免得上一条记录的工单号 / 工序号被预填到另一台设备的「导出 PDF」里
         */
        query(options) {
            const opts = options || {};
            if (!opts.appliedRecord) {
                // 手动查询：作废在途的「按记录加载」，免得它晚到后覆盖用户刚查的内容
                this.recordSeq = (this.recordSeq || 0) + 1;
            }
            const code = (this.queryState.deviceCode || '').trim();
            if (!code) {
                this.toast(this.$t('label.sith.tpm.opcPointCurve.deviceCodeRequired'));
                return Promise.resolve();
            }
            const start = parseInputTime(this.queryState.start);
            const end = parseInputTime(this.queryState.end);
            if (!start || !end) {
                this.toast(this.$t('label.sith.tpm.opcPointCurve.timeRequired'));
                return Promise.resolve();
            }
            if (start.getTime() >= end.getTime()) {
                this.toast(this.$t('label.sith.tpm.opcPointCurve.startBeforeEnd'));
                return Promise.resolve();
            }
            this.loadingPoints = true;
            return this.$store.dispatch('api/get', {
                url: process.env.VUE_APP_EM_OPCCLIENT_URL + '/emOpcPointCurve/points?deviceCode=' + encodeURIComponent(code)
            }).then(vo => {
                const previous = this.device ? this.device.deviceCode : null;
                const points = (vo && vo.points) || [];
                const storedCode = (vo && vo.deviceCode) || code;
                const deviceChanged = !previous || previous.toLowerCase() !== storedCode.toLowerCase();
                this.device = {deviceCode: storedCode, deviceName: (vo && vo.deviceName) || '', points};
                // 与 device 同时换：查询失败时两者都保持上一次的，不会错配
                this.appliedRecord = opts.appliedRecord || null;
                this.resetHistory([start, end]);
                if (!points.length) {
                    this.queryState.points = [];
                    this.toast(this.noPointsMessage());
                    return;
                }
                // 回显订阅表里的原始写法（查询本身大小写不敏感）
                this.queryState.deviceCode = storedCode;
                const selectable = points.filter(p => p.hasHistory).map(p => p.nodeId);
                let checked;
                if (opts.points === 'all') {
                    checked = selectable;
                } else if (Array.isArray(opts.points)) {
                    checked = opts.points.filter(id => selectable.indexOf(id) !== -1);
                } else if (deviceChanged) {
                    checked = selectable.slice(0, 1);
                } else {
                    checked = this.queryState.points.filter(id => selectable.indexOf(id) !== -1);
                }
                this.queryState.points = checked;
                return this.syncHistory().then(() => this.syncDetail());
            }).catch(error => {
                // 接口错误 api/get 已统一提示；其它（代码异常）打日志
                if (!error || (!error.response && !error.request)) {
                    console.error(error);
                }
            }).finally(() => {
                this.loadingPoints = false;
            });
        },
        resetHistory(range) {
            this.historySeq++;
            this.pendingNodes = new Set();
            this.seriesByNode = {};
            this.appliedRange = range;
            // 新范围的视窗由图表重新报告
            this.lastView = null;
            this.clearDetail();
        },
        syncHistory() {
            if (!this.device || !this.appliedRange) {
                return Promise.resolve();
            }
            const missing = this.checkedInOrder
                .map(p => p.nodeId)
                .filter(id => !this.seriesByNode[id] && !this.pendingNodes.has(id));
            if (!missing.length) {
                return Promise.resolve();
            }
            const seq = this.historySeq;
            const pending = this.pendingNodes;
            missing.forEach(id => pending.add(id));
            this.historyInFlight++;
            return this.$store.dispatch('api/post', {
                url: process.env.VUE_APP_EM_OPCCLIENT_URL + '/emOpcPointCurve/history',
                data: {
                    nodeIds: missing,
                    start: toServerTime(this.appliedRange[0]),
                    end: toServerTime(this.appliedRange[1]),
                    raw: false
                },
                commonErrorHandle: false,
                consoleLog: false
            }).then(response => {
                if (seq !== this.historySeq) {
                    return;
                }
                const next = Object.assign({}, this.seriesByNode);
                ((response && response.data) || []).forEach(s => {
                    next[s.nodeId] = s;
                });
                // 后端跳过的（没有计算属性 / 未开历史）也记一条空序列，免得每次勾选都重查
                missing.forEach(id => {
                    if (!next[id]) {
                        next[id] = {nodeId: id, eocId: null, points: []};
                    }
                });
                this.seriesByNode = next;
            }).catch(error => {
                if (seq !== this.historySeq) {
                    return;
                }
                const response = error && error.response;
                if (response && response.status === 400 && response.data && response.data.message) {
                    this.toast(response.data.message);
                } else {
                    this.$store.dispatch('api/error', {error});
                }
            }).finally(() => {
                missing.forEach(id => pending.delete(id));
                this.historyInFlight--;
            });
        },
        /**
         * 由炉子加热工单跳入：回填设备编码、时间段并自动查询。
         * 新记录勾该设备全部有历史的点位；旧版「保存查询」迁移来的记录有 config 时按其保存的点位 / 曲线设置。
         */
        applyRecord(vo) {
            let config = null;
            if (vo.config) {
                try {
                    config = JSON.parse(vo.config);
                } catch (e) {
                    config = null;
                }
                if (!config || typeof config !== 'object' || Array.isArray(config)) {
                    config = null;
                }
            }
            const start = parseInputTime(vo.startTime);
            const end = parseInputTime(vo.endTime);
            const record = {
                id: vo.id,
                queryKey: vo.queryKey,
                orderCode: vo.orderCode,
                operationSeq: vo.operationSeq,
                projectCode: vo.projectCode || '',
                materialCode: vo.materialCode || ''
            };
            if (!start || !end) {
                // 没有时间段不会查询，设备 / 曲线保持原样；不能挂上这条记录，否则「导出 PDF」会把它预填到别的设备的图上
                this.appliedRecord = null;
                this.toast(this.$t('label.sith.tpm.opcPointCurve.recordNoTime', {key: vo.queryKey}));
                return Promise.resolve();
            }
            const deviceCode = String(vo.deviceCode || '').trim();
            // 旧记录没有 / 清空了点位（含 config.points = []）都按「全部点位」查
            const savedPoints = config && Array.isArray(config.points) ? config.points.filter(id => typeof id === 'string') : [];
            const points = savedPoints.length ? savedPoints : 'all';
            this.queryState.deviceCode = deviceCode;
            this.queryState.start = toInputTime(start);
            this.queryState.end = toInputTime(end);
            this.queryState.settings = config ? this.normalizeSettings(config.settings) : defaultCurveSettings();
            return this.query({points, appliedRecord: record}).then(() => {
                if (points === 'all' || !this.device || !this.devicePoints.length
                    || this.device.deviceCode.toLowerCase() !== deviceCode.toLowerCase()) {
                    return;
                }
                const missing = points.filter(id => this.queryState.points.indexOf(id) === -1).length;
                if (missing > 0) {
                    this.toast(this.$t('label.sith.tpm.opcPointCurve.savedPointsMissing', {count: missing}));
                }
            });
        },
        loadRecord(recordId) {
            const id = recordId === undefined || recordId === null ? '' : String(recordId).trim();
            if (!id) {
                return Promise.resolve();
            }
            if (!/^\d+$/.test(id)) {
                this.toast(this.$t('label.sith.tpm.opcPointCurve.recordNotFound'));
                return Promise.resolve();
            }
            // 代次：A→B 跳转或用户手动查询后，晚到的旧响应作废。
            // immediate watcher 先于 created，此处兜底初始化（非响应式）
            this.recordSeq = (this.recordSeq || 0) + 1;
            const seq = this.recordSeq;
            return fetchRecord(this.$store, id).then(vo => {
                if (seq !== this.recordSeq) {
                    return undefined;
                }
                return this.applyRecord(vo);
            }).catch(error => {
                if (seq !== this.recordSeq) {
                    return;
                }
                if (error && error.response && error.response.status === 404) {
                    this.toast(this.$t('label.sith.tpm.opcPointCurve.recordNotFound'));
                } else if (error && error.response) {
                    // fetchRecord 用 commonErrorHandle:false，其它 HTTP 错误在这里统一提示
                    this.$store.dispatch('api/error', {error});
                } else {
                    console.error(error);
                }
            });
        },
        normalizeSettings(saved) {
            const settings = defaultCurveSettings();
            if (!saved || typeof saved !== 'object') {
                return settings;
            }
            if ('span' in saved) {
                settings.span = toNumberOrNull(saved.span);
            }
            if (SPAN_UNIT_MS[saved.spanUnit]) {
                settings.spanUnit = saved.spanUnit;
            }
            settings.ymin = toNumberOrNull(saved.ymin);
            settings.ymax = toNumberOrNull(saved.ymax);
            settings.yunit = typeof saved.yunit === 'string' ? saved.yunit : '';
            const xinterval = toNumberOrNull(saved.xinterval);
            settings.xinterval = xinterval !== null && xinterval > 0 ? xinterval : null;
            return settings;
        },
        /**
         * 「导出 PDF」：设备取已查到的设备（「编码 名称」），时间范围取曲线实际查询的范围，曲线图取当前视图；
         * 由炉子加热工单跳入时预填其工单号 / 工序号 / 项目号 / 物料号
         */
        openPdfModal() {
            if (!this.canExportPdf) {
                return;
            }
            this.$refs.pdfModal.open({
                deviceCode: this.device.deviceCode,
                deviceText: this.deviceTitle,
                range: this.appliedRange,
                appliedQuery: this.appliedRecord,
                getChartImage: size => this.$refs.chart.getViewImage(size)
            });
        },
        resetView() {
            if (this.$refs.chart) {
                this.$refs.chart.resetView();
            }
        },
        exportExcel() {
            if (!this.canExport) {
                return;
            }
            this.exporting = true;
            exportOpcCurveExcel({
                store: this.$store,
                deviceCode: this.device.deviceCode,
                range: this.appliedRange,
                columns: this.checkedInOrder.map(p => ({
                    nodeId: p.nodeId,
                    header: p.name + '（' + p.nodeId + '）' + (p.unit ? ' [' + p.unit + ']' : '')
                })),
                labels: {
                    timeHeader: this.$t('label.sith.tpm.opcPointCurve.timeColumn'),
                    fileMiddle: this.$t('label.sith.tpm.opcPointCurve.fileMiddle')
                }
            }).then(({rowCount}) => {
                this.toast(this.$t('label.sith.tpm.opcPointCurve.exported', {rows: rowCount}), 'success');
            }).catch(error => {
                if (error && error.userMessage) {
                    this.toast(error.userMessage);
                } else if (error && error.empty) {
                    this.toast(this.$t('label.sith.tpm.opcPointCurve.exportEmpty'));
                } else if (error && error.tooLarge) {
                    this.toast(this.$t('label.sith.tpm.opcPointCurve.exportTooLarge',
                        {rows: error.rowCount, cols: error.columnCount}));
                } else if (error && !error.response) {
                    console.error(error);
                    this.toast(this.$t('label.sith.tpm.opcPointCurve.exportFailed'), 'danger');
                }
            }).finally(() => {
                this.exporting = false;
            });
        }
    }
};
</script>

<style scoped>
.opc-point-curve__query {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
}

.opc-point-curve__query label {
    font-weight: normal;
}

.opc-point-curve__query .input-group-text {
    white-space: nowrap;
    color: #495057;
    background-color: #f4f6fa;
}

.opc-point-curve__device {
    width: 24rem;
    flex-wrap: nowrap;
}

/* v-select 填满 input-group 剩余宽度，高度与 input-group-sm 对齐，左侧与前缀标签接缝 */
.opc-point-curve__device-select {
    flex: 1 1 auto;
    min-width: 0;
}

.opc-point-curve__device-select >>> .vs__dropdown-toggle {
    min-height: calc(1.5em + 0.5rem + 2px);
    padding: 0 0 1px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    background: #fff;
}

.opc-point-curve__device-select >>> .vs__selected,
.opc-point-curve__device-select >>> .vs__search {
    font-size: 0.875rem;
    margin-top: 2px;
}

.opc-point-curve__device-select >>> .vs__selected {
    min-width: 0;
    flex-wrap: nowrap;
}

/* 高亮行是蓝底白字，次要文字的 text-muted 灰色在蓝底上看不清 */
.opc-point-curve__device-select >>> .vs__dropdown-option--highlight .text-muted {
    color: rgba(255, 255, 255, 0.85) !important;
}

.opc-point-curve__device-select >>> .vs__dropdown-menu {
    min-width: 100%;
    width: auto;
    max-width: 32rem;
}

.opc-point-curve__range {
    width: auto;
    flex-wrap: nowrap;
}

.opc-point-curve__range .form-control {
    flex: 0 0 auto;
    width: 11.5rem;
}

/* 夹在两个输入框之间：左侧也要压掉 1px，否则与开始时间的边框叠成 2px */
.opc-point-curve__range-sep {
    margin-left: -1px;
}

.opc-point-curve__query .btn,
.opc-point-curve__chart-card .card-header .btn {
    white-space: nowrap;
}

.opc-point-curve__record {
    font-size: 0.8rem;
    font-weight: normal;
    white-space: nowrap;
}

.opc-point-curve__body {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
}

.opc-point-curve__points {
    flex: 0 0 30%;
    min-width: 280px;
    max-width: 440px;
    margin-right: 0.5rem;
    min-height: 0;
}

.opc-point-curve__points-body {
    overflow: auto;
    min-height: 0;
}

.opc-point-curve__table td {
    vertical-align: middle;
}

.opc-point-curve__node-id {
    word-break: break-all;
}

.opc-point-curve__clickable {
    cursor: pointer;
}

.opc-point-curve__swatch {
    display: inline-block;
    flex: 0 0 auto;
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

.opc-point-curve__chart-card {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
}

.opc-point-curve__chart-body {
    min-height: 0;
}

.opc-point-curve__chart-wrap {
    flex: 1 1 auto;
    min-height: 320px;
}

@media (max-width: 767.98px) {
    .opc-point-curve__body {
        flex-direction: column;
    }

    .opc-point-curve__device,
    .opc-point-curve__range {
        width: 100%;
    }

    .opc-point-curve__range .form-control {
        flex: 1 1 auto;
        width: 1%;
    }

    .opc-point-curve__points {
        flex: 0 0 auto;
        max-width: none;
        max-height: 40vh;
        margin-right: 0;
        margin-bottom: 0.5rem;
    }
}
</style>
